import { useState } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Sparkles, Copy, FileText, Building } from "lucide-react";
import { ResumePdfBuilder } from "../components/ResumePdfBuilder";

const RESUME_KINDS = [
  { v: "resume", l: "Executive Resume" },
  { v: "exec_summary", l: "Executive Summary" },
  { v: "founder_bio", l: "Founder Bio" },
  { v: "board_bio", l: "Board Bio" },
  { v: "cover_letter", l: "Cover Letter" },
];
const COMPANY_KINDS = [
  { v: "job_description", l: "Job Description" },
  { v: "hiring_plan", l: "Hiring Plan" },
  { v: "interview_questions", l: "Interview Questions" },
  { v: "org_chart", l: "Org Chart" },
];
const ROLE_FAMILIES = ["CEO", "CTO", "CMO", "CFO", "COO", "CPO", "CRO", "Chief AI Officer", "Head of AI", "VP Engineering"];

export default function AIStudio() {
  return (
    <div className="space-y-8" data-testid="ai-studio">
      <div>
        <div className="label-caps mb-2">AI Studio</div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-semibold">
          <Sparkles className="inline h-7 w-7 mr-1 text-primary" />
          Draft, polish, launch.
        </h1>
        <p className="text-muted-foreground mt-2">Draft your leadership materials and hiring artifacts, grounded in the Tribunal library.</p>
      </div>

      <Tabs defaultValue="pdf">
        <TabsList>
          <TabsTrigger value="pdf" data-testid="ai-tab-resume-pdf"><FileText className="h-3.5 w-3.5 mr-1" />Resume (PDF)</TabsTrigger>
          <TabsTrigger value="resume" data-testid="ai-tab-resume"><FileText className="h-3.5 w-3.5 mr-1" />Bios & Letters</TabsTrigger>
          <TabsTrigger value="company" data-testid="ai-tab-company"><Building className="h-3.5 w-3.5 mr-1" />Company Builder</TabsTrigger>
        </TabsList>
        <TabsContent value="pdf" className="mt-6"><ResumePdfBuilder /></TabsContent>
        <TabsContent value="resume" className="mt-6"><ResumeBuilder /></TabsContent>
        <TabsContent value="company" className="mt-6"><CompanyBuilder /></TabsContent>
      </Tabs>
    </div>
  );
}

function ResumeBuilder() {
  const [kind, setKind] = useState("resume");
  const [target, setTarget] = useState("");
  const [ctx, setCtx] = useState("");
  const [out, setOut] = useState("");
  const [busy, setBusy] = useState(false);

  const gen = async () => {
    setBusy(true); setOut("");
    try {
      const { data } = await api.post("/ai/resume", { kind, target_role: target, context: ctx });
      setOut(data.content);
    } catch { toast.error("Generation failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <Card className="p-6 space-y-4">
        <div>
          <Label>Artifact</Label>
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger data-testid="resume-kind" className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>{RESUME_KINDS.map((k) => <SelectItem key={k.v} value={k.v}>{k.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Target role (optional)</Label>
          <Input value={target} onChange={(e) => setTarget(e.target.value)} placeholder="e.g. CTO at a health AI startup" data-testid="resume-target" className="mt-1.5" />
        </div>
        <div>
          <Label>Extra context (optional)</Label>
          <Textarea rows={5} value={ctx} onChange={(e) => setCtx(e.target.value)} placeholder="Recent wins, focus areas, tone…" data-testid="resume-context" className="mt-1.5" />
        </div>
        <Button onClick={gen} disabled={busy} className="rounded-full w-full" data-testid="resume-generate">
          <Sparkles className="h-4 w-4 mr-1" />{busy ? "Generating…" : "Generate"}
        </Button>
      </Card>
      <Card className="p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="label-caps">Output</div>
          {out && (
            <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(out); toast.success("Copied"); }} data-testid="resume-copy">
              <Copy className="h-3.5 w-3.5 mr-1" />Copy
            </Button>
          )}
        </div>
        {busy ? (
          <div className="text-muted-foreground text-sm">Composing…</div>
        ) : out ? (
          <pre data-testid="resume-output" className="whitespace-pre-wrap text-sm font-sans leading-relaxed max-h-[500px] overflow-y-auto">{out}</pre>
        ) : (
          <div className="text-muted-foreground text-sm">Your polished draft appears here.</div>
        )}
      </Card>
    </div>
  );
}

function CompanyBuilder() {
  const [kind, setKind] = useState("job_description");
  const [family, setFamily] = useState("CTO");
  const [stage, setStage] = useState("Seed");
  const [ctx, setCtx] = useState("");
  const [out, setOut] = useState("");
  const [busy, setBusy] = useState(false);

  const gen = async () => {
    setBusy(true); setOut("");
    try {
      const { data } = await api.post("/ai/company", {
        kind, role_family: family, stage, company_context: ctx,
      });
      setOut(data.content);
    } catch { toast.error("Generation failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <Card className="p-6 space-y-4">
        <div>
          <Label>Artifact</Label>
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger data-testid="company-kind" className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>{COMPANY_KINDS.map((k) => <SelectItem key={k.v} value={k.v}>{k.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Role family</Label>
          <Select value={family} onValueChange={setFamily}>
            <SelectTrigger data-testid="company-family" className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>{ROLE_FAMILIES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Stage</Label>
          <Select value={stage} onValueChange={setStage}>
            <SelectTrigger data-testid="company-stage" className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["Pre-seed","Seed","Series A","Series B","Growth","Public"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Company context (optional)</Label>
          <Textarea rows={5} value={ctx} onChange={(e) => setCtx(e.target.value)} placeholder="What we do, team size, tech stack…" data-testid="company-context" className="mt-1.5" />
        </div>
        <Button onClick={gen} disabled={busy} className="rounded-full w-full" data-testid="company-generate">
          <Sparkles className="h-4 w-4 mr-1" />{busy ? "Generating…" : "Generate"}
        </Button>
      </Card>
      <Card className="p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="label-caps">Output</div>
          {out && (
            <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(out); toast.success("Copied"); }} data-testid="company-copy">
              <Copy className="h-3.5 w-3.5 mr-1" />Copy
            </Button>
          )}
        </div>
        {busy ? (
          <div className="text-muted-foreground text-sm">Composing…</div>
        ) : out ? (
          <pre data-testid="company-output" className="whitespace-pre-wrap text-sm font-sans leading-relaxed max-h-[500px] overflow-y-auto">{out}</pre>
        ) : (
          <div className="text-muted-foreground text-sm">Your polished draft appears here.</div>
        )}
      </Card>
    </div>
  );
}
